/**
 * Vorgänge (Vorschau + Freigabe, [MCP] 8.2, 9.8; Anhang A.operations).
 * `OperationResult` ist das gemeinsame Ergebnis aller Aktionswerkzeuge; `OperationRef.actionIdentity`
 * spiegelt `mcp_operation.action_identity` (NULL für interne Aktionen, R-33).
 */
import { z } from 'zod';
import type { ActionKey } from './actions.js';
import type { DbTx, DomainContext, DomainResult, PersonId, UserId } from './common.js';
import type { HeldLocks } from './locks.js';

export const OperationStatusSchema = z.enum([
  'pending',
  'executed',
  'approved',
  'completed',
  'abandoned',
  'lapsed',
  'executing',
  'failed',
  'unknown',
  'rejected',
  'expired',
  'conflict',
  'superseded',
  'withdrawn',
]);
export type OperationStatus = z.infer<typeof OperationStatusSchema>;

export const Warning = z.object({
  code: z.enum([
    'CONTAINS_URL',
    'CONTAINS_CONTACT_DATA',
    'PHRASE_FILTER_HITS',
    'NO_CONSENT_RECORDED',
    'ART14_INFO_DUE',
    'CLASSIFICATION_UNCLEAR',
    'RECENT_CONTACT',
    'DAILY_LIMIT_NEAR',
    'LONG_TEXT',
    'AUTO_DOWNGRADED',
  ]),
  message: z.string(),
});

export const ToolError = z.object({
  code: z.enum([
    'NOT_FOUND',
    'INVALID_INPUT',
    'CONFLICT',
    'EXPIRED',
    'RATE_LIMITED',
    'LIMIT_REACHED',
    'CONSENT_REQUIRED',
    'OPTED_OUT',
    'ALREADY_DONE',
    'ACTION_DISABLED',
    'BLOCKED_PERSON',
    'QUALITY_CHECK_FAILED',
    'CAPABILITY_MISSING',
    'UNAVAILABLE',
  ]),
  message: z.string(),
  retry_after_s: z.int().optional(),
  request_id: z.uuid(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export const OperationResult = <P extends z.ZodType>(preview: P) =>
  z.object({
    operation_id: z.uuid(),
    action_key: z.string(),
    status: OperationStatusSchema,
    approval_mode: z.enum(['require', 'auto']),
    reused: z.boolean(),
    superseded_operation_id: z.uuid().nullable(),
    execution_mode: z.enum(['internal', 'manual_outbound', 'api_outbound']),
    approval_url: z.url().nullable(),
    expires_at: z.iso.datetime().nullable(),
    summary: z.string().max(500),
    preview: preview.nullable(),
    result: z.record(z.string(), z.unknown()).nullable(),
    warnings: z.array(Warning),
    hinweise: z.array(z.string()),
    demo: z.boolean(),
  });
export type OperationResultT = z.infer<ReturnType<typeof OperationResult<z.ZodUnknown>>>;

export type OperationRef = {
  id: string;
  userId: UserId;
  actionKey: ActionKey;
  status: OperationStatus;
  payload: unknown;
  actionIdentity: string | null;
};
/** `deepLink` ist im Demo-Modus `null` (E-o: Text ja, Link nein). */
export type HandoffResult = { text: string; deepLink: string | null; art14Appended: boolean };
export type HandoffOptions = { taskId?: string; confirmations?: { not_promotional?: true } };

export interface Operations {
  submit(
    ctx: DomainContext,
    key: ActionKey,
    input: unknown,
    meta: {
      triggerOrigin: 'mcp' | 'system' | 'third_party_content';
      clientRequestId?: string;
      grantId?: string;
    },
  ): Promise<DomainResult<OperationResultT>>;
  submitFromDashboard(
    ctx: DomainContext,
    key: ActionKey,
    input: unknown,
    meta: { sessionId: string; confirmations?: { not_promotional?: true } },
  ): Promise<DomainResult<OperationResultT>>;
  /**
   * v11: lädt den Vorgang mit user_id = ctx.userId; fremde/fehlende ID → NOT_FOUND, anderer Status →
   * CONFLICT. v12 (V12-16): nur innerhalb `packages/operations` aufrufbar (Architekturtest), da der Aufrufer
   * `op-user` halten muss; Domänenmodule außerhalb schließen über `completeFromSource` ab. Mandantenweite
   * Übergänge (Lebenszyklus-Hooks, Ablauf-Cron) laufen paketintern ohne diesen Port.
   */
  transition(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    opId: string,
    from: OperationStatus,
    to: OperationStatus,
    reason?: string,
  ): Promise<DomainResult<void>>;
  handoff(
    ctx: DomainContext,
    opId: string,
    a?: HandoffOptions,
  ): Promise<DomainResult<HandoffResult>>;
  handoffInTx(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    opId: string,
    a?: HandoffOptions,
  ): Promise<DomainResult<HandoffResult>>;
  /**
   * v12 (V12-16, C-C2): Abschluss aus Sendeerkennung oder Nutzerbestätigung der Domänenmodule (TP2/TP3/TP5);
   * öffnet eine **eigene** Transaktion (Gate geteilt, `op-user`, Vorgangsschlüssel, dann die Schlüssel des
   * Varianten-Handlers; Sperrplan V12-30) und ist damit nie Teil einer Domänentransaktion. Nur aus `approved`
   * → `completed`; `completed` → `ok` (idempotent, keine zweite Wirkung); jeder andere Status → `CONFLICT`;
   * fremde oder unbekannte ID → `NOT_FOUND`. Domänenmodule rufen sie nicht direkt, sondern über das
   * Outbox-Ereignis `operation.completion_requested` (Abonnent `operations`).
   */
  completeFromSource(
    ctx: DomainContext,
    opId: string,
    a: { source: 'send_detected' | 'user_confirmed'; interactionId?: string },
  ): Promise<DomainResult<{ status: OperationStatus }>>;
}

/**
 * Lesezugriff auf offene Vorgänge einer Person für `person_get.open_operations` (C-E1, Plan Welle 2 E RW-E-1; Anbieter
 * `operations`, O-E1). Liefert die IDs der **offenen** Vorgänge des Aufrufers zur Person: offen sind `pending` (wartet auf
 * Freigabe) und `approved` (freigegeben, noch nicht abgeschlossen); neueste zuerst (Anlagezeitpunkt, bei Gleichstand nach
 * ID absteigend), höchstens `a.limit` (Ganzzahl 1 bis 100, sonst `DomainFailure('INVALID_INPUT')`). Fremde, unbekannte,
 * eingeschränkte und gelöschte Personen ergeben `[]`; der Datenmodus des Kontexts trennt Live- und Demo-Daten.
 */
export interface OperationRefQuery {
  openForPerson(ctx: DomainContext, personId: PersonId, a: { limit: number }): Promise<string[]>;
}
